import { gql } from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useMeContext } from '~/modules/me/useMeContext';
import { ResourceAllocationStatus } from '~/types';
import { getTotalHoursForDateRangeFromScheduleRules } from '~/modules/resourcing/common/util';

export const PROJECT_ALLOCATION_FOR_RESOURCE_USER = gql`
  query Eager_GetAllocations(
    $projectUri: String
    $allocationStatusList: [ResourceAllocationStatus]
    $filter: ResourceAllocationFilter
  ) {
    resourceAllocations(
      projectUri: $projectUri
      allocationStatusList: $allocationStatusList
      filter: $filter
    ) {
      resourceAllocations {
        id
        scheduleRules {
          dateRange {
            startDate
            endDate
          }
          do
        }
      }
    }
  }
`;

export const PROJECT_ALLOCATION_FOR_RESOURCE_USER_V2 = gql`
  query Eager_GetAllocations2(
    $projectUri: String
    $allocationStatusList: [ResourceAllocationStatus]
    $filter: ResourceAllocationFilter
  ) {
    resourceAllocations: resourceAllocations2(
      projectUri: $projectUri
      allocationStatusList: $allocationStatusList
      filter: $filter
    ) {
      resourceAllocations {
        id
        scheduleRules {
          dateRange {
            startDate
            endDate
          }
          do
        }
      }
    }
  }
`;

const getProjectAllocationHours = async ({
  apolloClient,
  projectId,
  userId,
  startDate,
  endDate,
  useResourceAllocationsV2Query = false
}) => {
  const query = useResourceAllocationsV2Query
    ? PROJECT_ALLOCATION_FOR_RESOURCE_USER_V2
    : PROJECT_ALLOCATION_FOR_RESOURCE_USER;

  const {
    data: {
      resourceAllocations: { resourceAllocations }
    }
  } = await apolloClient.query({
    query,
    variables: {
      filter: {
        users: [userId]
      },
      projectUri: projectId,
      allocationStatusList: [ResourceAllocationStatus.Committed]
    },
    fetchPolicy: 'network-only'
  });

  if (!resourceAllocations?.length) return null;

  return getTotalHoursForDateRangeFromScheduleRules({
    scheduleRules: resourceAllocations[0].scheduleRules,
    start: startDate,
    end: endDate
  });
};

export const useProjectAllocationHours = ({
  projectId,
  userId,
  startDate,
  endDate,
  skip
}) => {
  const {
    featureFlags: { isPsaRmpEnhancedProjectManagerPersonaEnabled }
  } = useMeContext();
  const query = isPsaRmpEnhancedProjectManagerPersonaEnabled
    ? PROJECT_ALLOCATION_FOR_RESOURCE_USER_V2
    : PROJECT_ALLOCATION_FOR_RESOURCE_USER;

  const { loading, data } = useQuery(query, {
    variables: {
      filter: {
        users: [userId]
      },
      projectUri: projectId,
      allocationStatusList: [ResourceAllocationStatus.Committed]
    },
    skip
  });

  return {
    loading,
    projectAllocationHoursInDateRange: getTotalHoursForDateRangeFromScheduleRules(
      {
        scheduleRules:
          data?.resourceAllocations?.resourceAllocations?.[0]?.scheduleRules ||
          [],
        start: startDate,
        end: endDate
      }
    )
  };
};

export default getProjectAllocationHours;
